import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowRight, FaAtom, FaDatabase, FaDownload, FaGithub, FaHome } from "react-icons/fa";
import { useGlobalState } from "../Context";
import bindingEnergyPlot from "./Binding-Energy_per-Nucleon.jpg";
import headerImage from "../../Assets/header3.png";

const Hero = () => {
    const navigate = useNavigate();
    const { Z, setZ, N, setN } = useGlobalState();
    const [errors, setErrors] = useState({});

    const scrollToSection = (id) => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const calculate = (event) => {
        event?.preventDefault();
        const protonNumber = Number(Z);
        const neutronNumber = Number(N);
        const nextErrors = {};

        if (Z === undefined || Z === "" || !Number.isInteger(protonNumber) || protonNumber < 0) {
            nextErrors.z = "Enter a non-negative whole number.";
        }
        if (N === undefined || N === "" || !Number.isInteger(neutronNumber) || neutronNumber < 0) {
            nextErrors.n = "Enter a non-negative whole number.";
        }

        setErrors(nextErrors);
        if (Object.keys(nextErrors).length === 0) navigate("/calulations");
    };

    return (
        <main className="landing-page">
            <header className="site-header">
                <div className="nav-shell">
                    <button className="brand" type="button" onClick={() => scrollToSection("top")} aria-label="Binding Energy Calculator home">
                        <span className="brand-mark">BE</span>
                        <span><strong>Binding Energy</strong><small>Interactive nuclear physics</small></span>
                    </button>
                    <nav className="nav-links" aria-label="Primary navigation">
                        <button type="button" onClick={() => scrollToSection("calculator")}>Calculator</button>
                        <button type="button" onClick={() => scrollToSection("concept")}>Concept</button>
                        <button type="button" onClick={() => scrollToSection("data")}>Data</button>
                        <button type="button" onClick={() => scrollToSection("contact")}>Contact</button>
                    </nav>
                    <a className="nav-home" href="https://csu-physics.github.io/" aria-label="CSU Physics projects"><FaHome /></a>
                </div>
            </header>

            <section className="hero-section" id="top">
                <img src={headerImage} alt="Atomic nucleus surrounded by nuclear physics equations" className="hero-image" />
                <div className="hero-overlay" />
                <div className="hero-content">
                    <p className="eyebrow">Open computational physics tool</p>
                    <h1>Nuclear Binding Energy Calculator</h1>
                    <p className="hero-lede">Explore nuclear stability by comparing experimental mass-defect results with the semi-empirical liquid-drop model.</p>
                    <div className="hero-actions">
                        <button className="button button-primary" type="button" onClick={() => scrollToSection("calculator")}>Calculate binding energy <FaArrowRight /></button>
                        <a className="button button-secondary" href="https://github.com/CSU-Physics/binding-energy" target="_blank" rel="noreferrer"><FaGithub /> View source</a>
                    </div>
                    <dl className="hero-facts">
                        <div><dt>3,356</dt><dd>tabulated nuclides</dd></div>
                        <div><dt>2</dt><dd>calculation methods</dd></div>
                        <div><dt>IAEA</dt><dd>nuclear data source</dd></div>
                    </dl>
                </div>
            </section>

            <div className="content-shell">
                <section className="calculator-section" id="calculator">
                    <div className="section-copy">
                        <p className="section-kicker">Interactive calculator</p>
                        <h2>Choose a nuclide by its proton and neutron numbers.</h2>
                        <p>The calculator checks the IAEA Nuclear Data Services dataset and compares two estimates: one obtained from the measured atomic mass and another from the liquid-drop model.</p>
                        <ul className="method-list">
                            <li><FaDatabase /><span><strong>Mass-defect approach</strong>Uses tabulated atomic mass to determine the energy released when the nucleus forms.</span></li>
                            <li><FaAtom /><span><strong>Liquid-drop model</strong>Uses a semi-empirical formula based on volume, surface, Coulomb, asymmetry, and pairing terms.</span></li>
                        </ul>
                    </div>

                    <form className="calculator-card" onSubmit={calculate} noValidate>
                        <div className="calculator-heading">
                            <span>Nuclide input</span>
                            <p>Enter whole numbers for Z and N.</p>
                        </div>
                        <label htmlFor="protons">Number of protons <span>Z</span></label>
                        <input id="protons" type="number" min="0" step="1" inputMode="numeric" placeholder="e.g. 28" value={Z ?? ""} onChange={(event) => setZ(event.target.value)} aria-describedby={errors.z ? "proton-error" : undefined} aria-invalid={Boolean(errors.z)} />
                        {errors.z && <p className="field-error" id="proton-error">{errors.z}</p>}
                        <label htmlFor="neutrons">Number of neutrons <span>N</span></label>
                        <input id="neutrons" type="number" min="0" step="1" inputMode="numeric" placeholder="e.g. 34" value={N ?? ""} onChange={(event) => setN(event.target.value)} aria-describedby={errors.n ? "neutron-error" : undefined} aria-invalid={Boolean(errors.n)} />
                        {errors.n && <p className="field-error" id="neutron-error">{errors.n}</p>}
                        <button type="submit">Calculate binding energy <FaArrowRight /></button>
                        <p className="form-hint">Try Z = 28 and N = 34 for nickel-62.</p>
                    </form>
                </section>

                <section className="concept-section" id="concept">
                    <div className="section-heading">
                        <div><p className="section-kicker">The physics</p><h2>What is nuclear binding energy?</h2></div>
                        <p>Nuclear binding energy is the energy required to separate a nucleus into its constituent protons and neutrons—or, equivalently, the energy released when those nucleons form the nucleus.</p>
                    </div>
                    <div className="concept-grid">
                        <article><span>01</span><h3>Mass defect</h3><p>A bound nucleus has less mass than its separated nucleons. Einstein’s mass–energy equivalence converts this difference into binding energy.</p><div className="formula">B.E. = Δm c<sup>2</sup></div></article>
                        <article><span>02</span><h3>Energy per nucleon</h3><p>Dividing total binding energy by mass number A provides a useful measure of nuclear stability: larger values generally indicate more tightly bound nuclei.</p><div className="formula">B.E./A</div></article>
                        <article><span>03</span><h3>A familiar example</h3><p>The deuteron contains one proton and one neutron. About 2.23 MeV is required to separate them, and the same energy is released when the deuteron forms.</p><div className="formula">²H → p + n</div></article>
                    </div>
                </section>

                <section className="data-section" id="data">
                    <div className="data-copy">
                        <p className="section-kicker">The nuclear landscape</p>
                        <h2>Binding energy reveals where nuclei are most stable.</h2>
                        <p>The plot shows binding energy per nucleon versus mass number for 3,356 nuclides with tabulated mass and binding-energy values. The curve rises rapidly for light nuclei, peaks near nickel-62, and then decreases gradually for heavier systems—helping explain why both fusion and fission can release energy.</p>
                        <div className="data-links">
                            <a href="https://nds.iaea.org/" target="_blank" rel="noreferrer"><FaDatabase /> IAEA Nuclear Data Services</a>
                            <a href="https://github.com/CSU-Physics/binding-energy/blob/main/BE_A.data" target="_blank" rel="noreferrer"><FaDownload /> Data file</a>
                            <a href="https://github.com/CSU-Physics/binding-energy/blob/main/BE_A.agr" target="_blank" rel="noreferrer"><FaDownload /> XmGrace file</a>
                        </div>
                    </div>
                    <figure className="data-figure">
                        <img src={bindingEnergyPlot} alt="Binding energy per nucleon plotted against mass number" />
                        <figcaption>Binding energy per nucleon (MeV) as a function of mass number A.</figcaption>
                    </figure>
                </section>

                <section className="contact-section" id="contact">
                    <div className="contact-intro">
                        <p className="section-kicker section-kicker-light">Contact</p>
                        <h2>Interested in educational or research collaboration?</h2>
                        <p>I welcome inquiries about educational and research collaborations of any kind, including joint projects, computational methods, classroom applications, and opportunities to extend these open resources.</p>
                    </div>
                    <address className="contact-card">
                        <div className="contact-avatar" aria-hidden="true">MH</div>
                        <div className="contact-card-copy">
                            <p className="contact-name">Dr. Mohammadreza Hadizadeh</p>
                            <p className="contact-title">Professor of Physics</p>
                            <p className="contact-affiliation">College of Engineering, Science, Technology and Agriculture<br />Central State University</p>
                            <dl className="contact-details">
                                <div><dt>O</dt><dd><a href="tel:+19373766273">937 376-6273</a></dd></div>
                                <div><dt>E</dt><dd><a href="mailto:mhadizadeh@centralstate.edu">mhadizadeh@centralstate.edu</a></dd></div>
                                <div><dt>W</dt><dd><a href="https://people.ohio.edu/hadizadm/" target="_blank" rel="noopener noreferrer">https://people.ohio.edu/hadizadm/ <span aria-hidden="true">↗</span></a></dd></div>
                            </dl>
                        </div>
                    </address>
                </section>
            </div>

            <footer className="site-footer"><span>Nuclear Binding Energy Calculator</span><span>CSU Physics · Central State University</span></footer>
        </main>
    );
};

export default Hero;
